import { useState, useEffect, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于BaoyuTV',
  description: 'BaoyuTV品牌介绍、信誉展示与联系方式。',
  url: 'https://baoyutv.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: 'BaoyuTV',
    url: 'https://baoyutv.com',
  },
};

const milestones = [
  { year: '2019', title: '平台创立', desc: 'BaoyuTV正式上线，以高清视频为核心切入市场。' },
  { year: '2020', title: '用户突破百万', desc: '凭借优质内容和流畅体验，注册用户突破100万。' },
  { year: '2021', title: '内容生态扩展', desc: '新增动漫、纪录片频道，合作内容提供商超过50家。' },
  { year: '2022', title: '技术升级', desc: '全面支持4K HDR播放，全球CDN节点扩展至150+。' },
  { year: '2023', title: '千万用户', desc: '月活跃用户突破3000万，成为行业领先平台。' },
  { year: '2025', title: '智能时代', desc: 'AI推荐算法与智能字幕上线，用户体验再升级。' },
];

const reputationItems = [
  { icon: 'ri-shield-star-line', title: '权威认证', desc: '通过国家信息安全等级保护三级认证，用户数据安全有保障。' },
  { icon: 'ri-award-line', title: '行业奖项', desc: '连续三年荣获"最佳视频平台用户体验奖"，行业口碑卓越。' },
  { icon: 'ri-user-heart-line', title: '用户信赖', desc: 'NPS净推荐值达72分，用户满意度行业领先。' },
  { icon: 'ri-shake-hands-line', title: '合作伙伴', desc: '与超过100家影视内容提供商建立深度合作关系。' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9q4facnlsngrm4llr90', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - BaoyuTV高清视频平台"
        description="了解BaoyuTV品牌故事、发展历程、行业信誉及联系方式。BaoyuTV致力于为用户提供海量高清影视资源与优质观影体验。"
        keywords="baoyutv,关于我们,品牌介绍,联系方式,视频平台"
        canonicalUrl="https://baoyutv.com/about"
        ogTitle="关于我们 - BaoyuTV高清视频平台"
        ogDescription="了解BaoyuTV品牌故事、发展历程、行业信誉及联系方式。"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 pt-64 md:pt-72 pb-10 relative overflow-hidden">
          <div className="absolute inset-0 top-0 h-full">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20office%20team%20collaboration%20scene%20with%20warm%20amber%20lighting%2C%20creative%20workspace%20atmosphere%2C%20dark%20background%2C%20professional%20and%20friendly%20vibe%2C%20cinematic%20composition&width=1920&height=600&seq=about-hero-001&orientation=landscape"
              alt="关于BaoyuTV"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
              关于 <span className="text-primary-400">BaoyuTV</span>
            </h1>
            <p className="text-sm md:text-base text-white/70 max-w-xl mx-auto">
              以技术驱动观影体验，用内容连接万千用户
            </p>
          </div>
        </div>

        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                  品牌<span className="text-primary-500">故事</span>
                </h2>
                <div className="space-y-4 text-foreground-600 leading-relaxed">
                  <p>
                    BaoyuTV成立于2019年，由一群热爱影视技术的工程师和内容创作者共同创立。我们深知用户对高清画质和流畅体验的渴望，因此从创立之初便致力于打造一个以技术驱动的视频平台。
                  </p>
                  <p>
                    经过多年发展，BaoyuTV已成长为国内领先的高清视频平台，月活跃用户超过5000万。平台拥有超过10万部影视资源，涵盖国产剧、韩剧、美剧、日漫、国漫、综艺、纪录片等全品类内容。
                  </p>
                  <p>
                    我们始终坚持"用户至上、技术驱动"的理念，不断优化播放体验，引入4K HDR画质、智能码率自适应、AI个性化推荐等前沿技术，让每一位用户都能享受到极致的观影体验。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden h-80 md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20team%20brainstorming%20in%20modern%20office%20with%20warm%20lighting%2C%20collaborative%20atmosphere%2C%20dark%20background%20elements%2C%20professional%20cinematic%20photography%20style%2C%20clean%20composition&width=800&height=600&seq=about-story-001&orientation=landscape"
                  alt="BaoyuTV团队"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                发展<span className="text-primary-500">历程</span>
              </h2>
              <p className="text-sm text-foreground-500">一路走来，与用户共同成长</p>
            </div>

            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden md:block"></div>
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div key={index} className={`flex flex-col md:flex-row items-center gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="flex-1 md:text-right">
                      <div className={`bg-background-50 border border-background-200/70 rounded-xl p-5 ${index % 2 === 1 ? 'md:text-left' : 'md:text-right'}`}>
                        <span className="text-primary-500 font-heading text-lg font-bold">{item.year}</span>
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mt-1">{item.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    </div>
                    <div className="w-4 h-4 rounded-full bg-primary-500 flex-shrink-0 relative z-10"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                行业<span className="text-accent-500">信誉</span>
              </h2>
              <p className="text-sm text-foreground-500">权威认证与用户口碑，值得信赖</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {reputationItems.map((item, index) => (
                <div key={index} className="text-center p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                <span className="text-primary-500">联系</span>我们
              </h2>
              <p className="text-sm text-foreground-500">
                如有任何问题或合作意向，欢迎随时与我们联系
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
              {[
                { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@baoyutv.com' },
                { icon: 'ri-customer-service-2-line', label: '客服热线', value: '400-888-8888' },
                { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区科技园区' },
              ].map((item, index) => (
                <div key={index} className="text-center p-5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <div className="text-xs text-foreground-400 mb-1">{item.label}</div>
                  <div className="text-sm font-medium text-foreground-800">{item.value}</div>
                </div>
              ))}
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入留言主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {submitting ? '提交中...' : '发送留言'}
              </button>

              {formStatus === 'success' && (
                <div className="text-center py-3 rounded-lg bg-secondary-100 text-secondary-700 text-sm">
                  感谢您的留言！我们会尽快与您取得联系。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="text-center py-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                  {formError || '提交失败，请稍后重试'}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}